import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminApi, type SupportRequestDetail } from "@/services/adminApi";
import { cn } from "@/lib/utils";

const STATUSES = ["OPEN", "TRIAGED", "LINKED_TO_ISSUE", "RESOLVED", "CLOSED"] as const;
const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;

const STATUS_META: Record<string, { label: string; dot: string; chip: string }> = {
  OPEN: { label: "Open", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 ring-amber-200/60" },
  TRIAGED: { label: "Triaged", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 ring-sky-200/60" },
  LINKED_TO_ISSUE: { label: "Linked", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700 ring-violet-200/60" },
  RESOLVED: { label: "Resolved", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/60" },
  CLOSED: { label: "Closed", dot: "bg-zinc-400", chip: "bg-zinc-50 text-zinc-600 ring-zinc-200/60" },
};

const TYPE_LABEL: Record<string, string> = {
  BUG: "Bug",
  AI_RESPONSE: "Bad AI answer",
  FEATURE_REQUEST: "Feature request",
  BILLING_ACCOUNT: "Billing & account",
  GENERAL_FEEDBACK: "General feedback",
};

export default function SupportRequestDetailPage() {
  const { id = "" } = useParams();
  const [data, setData] = useState<SupportRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminApi.getSupportRequest(id));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [data?.messages?.length]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await adminApi.replySupportRequest(id, reply);
      setReply("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onStatus = async (status: string) => {
    setData(await adminApi.updateSupportStatus(id, status));
    await load();
  };
  const onSeverity = async (severity: string) => {
    setData(await adminApi.updateSupportSeverity(id, severity));
  };

  const createLinear = async () => {
    setBusy(true);
    setError(null);
    try {
      setData(await adminApi.createSupportLinearIssue(id));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to create Linear issue");
    } finally {
      setBusy(false);
    }
  };

  const downloadAttachment = async (attachmentId: string) => {
    const { url } = await adminApi.getSupportAttachmentDownloadUrl(id, attachmentId);
    window.open(url, "_blank");
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#fafaf9]">
        <div className="mx-auto max-w-6xl px-8 py-10">
          <div className="h-6 w-32 animate-pulse rounded bg-zinc-200/70" />
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-3">
              <div className="h-64 animate-pulse rounded-2xl bg-zinc-100" />
              <div className="h-40 animate-pulse rounded-2xl bg-zinc-100" />
            </div>
            <div className="space-y-3">
              <div className="h-48 animate-pulse rounded-2xl bg-zinc-100" />
              <div className="h-32 animate-pulse rounded-2xl bg-zinc-100" />
            </div>
          </div>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafaf9] text-zinc-500">
        Not found.
      </div>
    );

  const status = STATUS_META[data.status] ?? STATUS_META.CLOSED;

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="mx-auto max-w-6xl px-8 py-10">
        {/* Back link */}
        <Link
          to="/admin/support"
          className="group mb-6 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-500 transition-colors hover:text-zinc-900"
        >
          <svg className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 6-6 6 6 6" />
          </svg>
          Back to inbox
        </Link>

        {/* Title row */}
        <div className="mb-8 flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-zinc-500">
              <span>{TYPE_LABEL[data.type] ?? data.type.replace(/_/g, " ")}</span>
              <span className="text-zinc-300">·</span>
              <span className="font-mono text-[10.5px] normal-case tracking-normal text-zinc-400">#{data.id.slice(0, 8)}</span>
            </div>
            <h1 className="mt-2 text-[24px] font-semibold tracking-[-0.018em] text-zinc-900">
              {data.title}
            </h1>
            <p className="mt-1 text-[12.5px] text-zinc-500">
              Opened {new Date(data.createdAt).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-medium ring-1 ring-inset ${status.chip}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Thread */}
          <div className="lg:col-span-2">
            <div className="flex h-[720px] flex-col overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
              {/* Conversation header — customer info */}
              <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50/40 px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-200/70 text-[12px] font-semibold uppercase text-zinc-700 ring-1 ring-zinc-200">
                    {(data.user?.email?.[0] ?? "U").toUpperCase()}
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-900 leading-tight">
                      {data.user?.email ?? "Unknown user"}
                    </p>
                    {data.organization?.name && (
                      <p className="mt-0.5 text-[11.5px] text-zinc-500 leading-tight">
                        {data.organization.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[11.5px] text-zinc-400">
                  <span className="tabular-nums">
                    {data.messages.length} {data.messages.length === 1 ? "msg" : "msgs"}
                  </span>
                </div>
              </div>

              <div
                ref={scrollRef}
                className="flex-1 space-y-2 overflow-y-auto bg-[#fafaf9]/40 px-5 py-5"
              >
                {data.messages.map((m, idx) => {
                  const isSupport = m.authorType === "SUPERADMIN";
                  const isSystem = m.authorType === "SYSTEM";
                  // Layout convention: customer (user) on LEFT, Libra Support on RIGHT.
                  const onRight = isSupport;
                  const name = isSupport
                    ? "Libra Support"
                    : isSystem
                      ? "System"
                      : data.user?.email ?? "User";
                  const initial = isSupport ? "L" : (name[0] ?? "?").toUpperCase();
                  const prev = data.messages[idx - 1];
                  const next = data.messages[idx + 1];
                  const firstInGroup = !prev || prev.authorType !== m.authorType;
                  const lastInGroup = !next || next.authorType !== m.authorType;

                  const avatarColor = isSupport
                    ? "bg-orange-50 text-orange-700 ring-orange-200/60"
                    : isSystem
                      ? "bg-zinc-50 text-zinc-600 ring-zinc-200/60"
                      : "bg-zinc-100 text-zinc-700 ring-zinc-200/60";

                  const bubbleColor = isSupport
                    ? "bg-zinc-900 text-zinc-50"
                    : isSystem
                      ? "bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200/60"
                      : "bg-white text-zinc-900 ring-1 ring-inset ring-zinc-200/60";

                  const cornerClass = onRight
                    ? cn(
                        firstInGroup && lastInGroup && "rounded-2xl rounded-br-md",
                        firstInGroup && !lastInGroup && "rounded-2xl rounded-br-md",
                        !firstInGroup && lastInGroup && "rounded-2xl rounded-tr-md rounded-br-md",
                        !firstInGroup && !lastInGroup && "rounded-2xl rounded-tr-md rounded-br-md",
                      )
                    : cn(
                        firstInGroup && lastInGroup && "rounded-2xl rounded-bl-md",
                        firstInGroup && !lastInGroup && "rounded-2xl rounded-bl-md",
                        !firstInGroup && lastInGroup && "rounded-2xl rounded-tl-md rounded-bl-md",
                        !firstInGroup && !lastInGroup && "rounded-2xl rounded-tl-md rounded-bl-md",
                      );

                  return (
                    <div
                      key={m.id}
                      className={`flex w-full gap-2 ${onRight ? "flex-row-reverse" : "flex-row"} ${!firstInGroup ? "-mt-1" : ""}`}
                    >
                      <div className="w-7 shrink-0">
                        {lastInGroup && (
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-full text-[10.5px] font-semibold ring-1 ${avatarColor}`}
                          >
                            {initial}
                          </div>
                        )}
                      </div>
                      <div
                        className={`flex max-w-[78%] flex-col ${onRight ? "items-end" : "items-start"}`}
                      >
                        {firstInGroup && (
                          <div
                            className={`mb-1 flex items-baseline gap-2 px-1 ${onRight ? "flex-row-reverse" : ""}`}
                          >
                            <span className="text-[12px] font-semibold text-zinc-900">{name}</span>
                            {isSupport && (m as any).authorUserId && (
                              <span className="font-mono text-[10px] text-zinc-400">
                                {(m as any).authorUserId.slice(0, 8)}
                              </span>
                            )}
                            <span className="text-[10.5px] text-zinc-400 tabular-nums">
                              {new Date(m.createdAt).toLocaleString(undefined, {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        )}
                        <div
                          className={`whitespace-pre-wrap break-words px-3.5 py-2 text-[13.5px] leading-[1.5] shadow-[0_1px_1px_rgba(0,0,0,0.02)] ${bubbleColor} ${cornerClass}`}
                        >
                          {m.body}
                        </div>
                        {lastInGroup && (
                          <span className="mt-1 px-1 text-[10px] text-zinc-400 tabular-nums">
                            {new Date(m.createdAt).toLocaleTimeString(undefined, {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply composer — admin replies as Libra Support, posted on the right */}
              <div className="border-t border-zinc-100 bg-white px-3 pb-3 pt-3">
                <div className="mb-2 flex items-center gap-1.5 px-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-zinc-500">
                  <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-50 text-[8.5px] font-bold text-orange-700 ring-1 ring-orange-200/60">
                    L
                  </span>
                  Replying as Libra Support
                </div>
                <div className="rounded-xl border border-zinc-200/80 bg-white transition-colors focus-within:border-zinc-400">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    rows={3}
                    placeholder="Write a reply…"
                    className="block w-full resize-none rounded-t-xl bg-transparent px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-zinc-800 placeholder:text-zinc-400 outline-none"
                  />
                  <div className="flex items-center justify-between border-t border-zinc-100 px-2 py-1.5">
                    <p className="px-1 text-[10.5px] text-zinc-400">
                      <kbd className="rounded bg-zinc-100 px-1 py-px font-mono text-[10px] text-zinc-600">
                        ⌘↵
                      </kbd>{" "}
                      to send · The user will see this as &ldquo;Libra Support&rdquo;
                    </p>
                    <button
                      onClick={sendReply}
                      disabled={busy || !reply.trim()}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-[12px] font-medium text-white transition-all hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400"
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m22 2-7 20-4-9-9-4z" />
                      </svg>
                      Send reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <Card title="Triage">
              <LabeledField label="Status">
                <SelectControl value={data.status} onChange={onStatus} options={STATUSES.map((s) => ({ value: s, label: STATUS_META[s].label }))} />
              </LabeledField>
              <LabeledField label="Severity">
                <SelectControl value={data.severity} onChange={onSeverity} options={SEVERITIES.map((s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase() }))} />
              </LabeledField>

              <div className="pt-1">
                {data.externalIssueUrl ? (
                  <a
                    href={data.externalIssueUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between rounded-lg border border-violet-200/60 bg-violet-50/50 px-3 py-2.5 transition-colors hover:bg-violet-50"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="h-3.5 w-3.5 text-violet-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
                        <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
                      </svg>
                      <span className="text-[12.5px] font-medium text-violet-900">
                        {data.externalIssueIdentifier ?? "Linked"}
                      </span>
                    </span>
                    <svg className="h-3.5 w-3.5 text-violet-700/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 17 17 7M8 7h9v9" />
                    </svg>
                  </a>
                ) : (
                  <button
                    onClick={createLinear}
                    disabled={busy}
                    className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200/80 bg-white px-3 py-2 text-[12.5px] font-medium text-zinc-700 transition-all hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Create Linear issue
                  </button>
                )}
                {error && <p className="mt-2 text-[11.5px] text-red-600">{error}</p>}
              </div>
            </Card>

            <Card title="Customer">
              <Meta label="User" value={data.user?.email} mono />
              <Meta label="Organization" value={data.organization?.name} />
            </Card>

            <Card title="Diagnostics">
              <Meta label="Route" value={data.routeUrl} mono />
              <Meta label="Release" value={data.releaseVersion} mono />
              <Meta label="Environment" value={data.environment} />
              <Meta label="Correlation" value={data.correlationId} mono small />
              <Meta label="Traceparent" value={data.traceparent} mono small />
              {data.chatId && <Meta label="Chat" value={data.chatId} mono small />}
              {data.messageId && <Meta label="Message" value={data.messageId} mono small />}
              {data.reasonTags && data.reasonTags.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">
                    Reasons
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {data.reasonTags.map((t) => (
                      <span key={t} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {data.attachments && data.attachments.length > 0 && (
              <Card title="Attachments">
                <ul className="space-y-1">
                  {data.attachments.map((a) => (
                    <li key={a.id}>
                      <button
                        onClick={() => downloadAttachment(a.id)}
                        className="group flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-zinc-50"
                      >
                        <svg className="h-3.5 w-3.5 shrink-0 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>
                        <span className="flex-1 truncate text-[12.5px] text-zinc-700 group-hover:text-zinc-900">
                          {a.fileName}
                        </span>
                        <svg className="h-3.5 w-3.5 shrink-0 text-zinc-300 transition-transform group-hover:translate-x-0.5 group-hover:text-zinc-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {(data as any).statusEvents && (data as any).statusEvents.length > 0 && (
              <Card title="Activity">
                <ol className="space-y-2.5">
                  {(data as any).statusEvents.map((evt: any) => {
                    const to = STATUS_META[evt.toStatus] ?? STATUS_META.CLOSED;
                    return (
                      <li key={evt.id} className="flex items-start gap-2.5 text-[11.5px]">
                        <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${to.dot}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-zinc-700">
                            {evt.fromStatus ? <><span className="text-zinc-400">{STATUS_META[evt.fromStatus]?.label}</span> → </> : null}
                            <span className="font-medium text-zinc-900">{to.label}</span>
                          </p>
                          <p className="mt-0.5 text-zinc-400 tabular-nums">
                            {new Date(evt.createdAt).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </Card>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function LabeledField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">{label}</p>
      {children}
    </div>
  );
}

function SelectControl({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full cursor-pointer appearance-none rounded-lg border border-zinc-200/80 bg-white py-1.5 pl-3 pr-8 text-[12.5px] font-medium text-zinc-800 transition-colors hover:border-zinc-300 focus:border-zinc-400 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

function Meta({
  label,
  value,
  mono,
  small,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  small?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.06em] text-zinc-400">{label}</p>
      <p
        className={`break-all text-[12.5px] text-zinc-800 ${mono ? "font-mono" : ""} ${
          small ? "text-[11px]" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
